import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Users, MagnifyingGlass, Chat, ArrowLeft } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const GroupListPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [groups, setGroups] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchGroups();
  }, [user, navigate]);

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/api/groups/list?user_id=${user.id}`);
      let groupList = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.groups)
        ? response.data.groups
        : [];
      
      // Filter out any withdrawal groups for all users on Groups list page
      groupList = groupList.filter(g => 
        !g.name?.toLowerCase().startsWith('withdraw group') &&
        g.group_type !== 'withdrawal'
      );
      
      setGroups(groupList);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredGroups = groups.filter(group =>
    group.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatLastMessage = (timestamp) => {
    if (!timestamp) return 'No messages yet';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 24) {
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-IN');
  };

  return (
    <div className="min-h-screen bg-wa-hover">
      {/* Header */}
      <div className="bg-green-600 text-white p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(user?.role === 'super_admin' ? '/admin-dashboard' : '/handler-dashboard')}
              className="hover:bg-green-500 p-2 rounded-full transition-colors"
            >
              <ArrowLeft size={24} weight="bold" />
            </button>
            <Users size={28} weight="bold" />
            <h1 className="text-xl font-bold">Groups</h1>
          </div>
          <button
            onClick={() => navigate('/groups/create')}
            className="bg-wa-panel text-green-600 px-4 py-2 rounded-full font-semibold flex items-center gap-2 hover:bg-wa-hover"
          >
            <Plus size={20} weight="bold" />
            New Group
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-4xl mx-auto p-4">
        <div className="relative">
          <MagnifyingGlass
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-wa-text-secondary"
          />
          <input
            type="text"
            placeholder="Search groups..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-lg border border-wa-border focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </div>
      </div>

      {/* Groups List */}
      <div className="max-w-4xl mx-auto px-4 pb-6">
        {loading ? (
          <div className="text-center py-8 text-wa-text-secondary">Loading groups...</div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-12">
            <Users size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-wa-text-secondary mb-4">
              {searchQuery ? 'No groups found' : 'No groups yet'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => navigate('/groups/create')}
                className="bg-green-600 text-white px-6 py-2 rounded-full font-semibold hover:bg-green-700"
              >
                Create Your First Group
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredGroups.map(group => (
              <div
                key={group.id}
                onClick={() => navigate(`/groups/${group.id}`)}
                className="bg-wa-panel rounded-lg p-4 shadow hover:shadow-md transition cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  {/* Group Icon */}
                  <div className="w-12 h-12 bg-wa-hover rounded-full flex items-center justify-center flex-shrink-0">
                    <Users size={24} className="text-green-600" weight="bold" />
                  </div>

                  {/* Group Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-semibold text-wa-text-primary truncate">
                        {group.name}
                      </h3>
                      <span className="text-xs text-wa-text-secondary ml-2 flex-shrink-0">
                        {formatLastMessage(group.last_message_at)}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-wa-text-secondary">
                      <Users size={16} />
                      <span>{group.members?.length || 0} members</span>
                    </div>

                    {group.description && (
                      <p className="text-sm text-wa-text-secondary mt-1 truncate">
                        {group.description}
                      </p>
                    )}
                  </div>

                  {/* Chat Icon */}
                  <Chat size={24} className="text-wa-text-secondary flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupListPage;
